// Import the core angular services.
import { ErrorHandler, Injectable, InjectionToken, inject } from "@angular/core";
// Import the application components and services.
import { ErrorLogService } from "./error-log.service";
import { isStaleBuildError, recoverFromStaleBuild } from "../common/stale-build-recovery";

export class LoggingErrorHandlerOptions {
  rethrowError: boolean;
  unwrapError: boolean;
}

export const LoggingErrorHandlerOptionsTokens = new InjectionToken("error.handler.options");

@Injectable({ providedIn: "root" })
export class LoggingErrorHandler implements ErrorHandler {
  private errorLogService: ErrorLogService;
  private options: LoggingErrorHandlerOptions;

  // I initialize the service.
  // --
  // CAUTION: The core implementation of the ErrorHandler class accepts a boolean
  // parameter, `rethrowError`; however, this is not part of the interface for the
  // class. In our version, we are supporting that same concept; but, we are doing it
  // through an Options object (which is being defaulted in the providers).
  constructor() {
    const errorLogService = inject(ErrorLogService);
    const options = inject<LoggingErrorHandlerOptions>(LoggingErrorHandlerOptions);

    this.errorLogService = errorLogService;
    this.options = options;
  }

  // I handle the given error.
  public handleError(error: any): void {
    const reportedError = this.options.unwrapError ? this.findOriginalError(error) : error;

    // Send to the error-logging service.
    try {
      this.errorLogService.logError(reportedError);
    } catch (loggingError) {
      console.group("ErrorHandler");
      console.warn("Error when trying to log error to", this.errorLogService);
      console.error(loggingError);
      console.groupEnd();
    }

    // A release renames every chunk and deletes the previous build, so a tab that outlived a deploy
    // cannot load code it has not needed yet. Reload rather than swallow the error and leave the
    // user on a half-rendered view until they hard-refresh.
    if (isStaleBuildError(reportedError) && recoverFromStaleBuild("The running build is no longer on the server")) {
      return;
    }

    if (this.options.rethrowError) {
      throw error;
    }
  }

  // I attempt to find the underlying error in the given Wrapped error.
  private findOriginalError(error: any): any {
    while (error && error.originalError) {
      error = error.originalError;
    }
    return error;
  }
}

export const LoggingErrorHandlerProviders = [
  {
    provide: LoggingErrorHandlerOptions,
    useValue: LoggingErrorHandlerOptions,
  },
  {
    provide: ErrorHandler,
    useClass: LoggingErrorHandler,
  },
];
